/**
 * API client.
 *
 * Two things this module is responsible for, so no component has to think
 * about them:
 *
 *  - Credentials. The session lives in an HttpOnly cookie, so every request
 *    sends `credentials: 'include'` and no token is ever held in JavaScript.
 *  - CSRF. The server sets a readable `ltsi_csrf` cookie; we echo it in the
 *    X-CSRF-Token header on every state-changing request.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session has gone and the user must sign in again. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  /** True when another tab saved first and the client must reload. */
  get isConflict() {
    return this.status === 409;
  }

  /** Field-level messages, when the server rejected a payload. */
  get fieldErrors(): Array<{ field: string; message: string }> {
    return Array.isArray(this.details) ? (this.details as never) : [];
  }
}

const CSRF_COOKIE = 'ltsi_csrf';

function readCookie(name: string): string | undefined {
  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=');
    if (index < 1) continue;
    if (part.slice(0, index).trim() !== name) continue;
    try {
      return decodeURIComponent(part.slice(index + 1).trim());
    } catch {
      return undefined;
    }
  }
  return undefined;
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: { signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (UNSAFE.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers['X-CSRF-Token'] = token;
  }

  const response = await fetch(path, {
    method,
    headers,
    // Sends the HttpOnly session cookie; the token itself is never readable.
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? 'UNKNOWN',
      error.message ?? `Request failed with ${response.status}`,
      error.details,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: { signal?: AbortSignal }) =>
    request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: { signal?: AbortSignal }) =>
    request<T>('POST', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: { signal?: AbortSignal }) =>
    request<T>('PATCH', path, body, options),
  del: <T>(path: string, options?: { signal?: AbortSignal }) =>
    request<T>('DELETE', path, undefined, options),
};

// ------------------------------------------------------------------ types

export type Role = 'ADMIN' | 'SALES';

export type ProposalStatus =
  | 'DRAFT'
  | 'READY'
  | 'FINAL'
  | 'SUBMITTED'
  | 'WON'
  | 'LOST'
  | 'EXPIRED';

export const PROPOSAL_STATUSES: ProposalStatus[] = [
  'DRAFT',
  'READY',
  'FINAL',
  'SUBMITTED',
  'WON',
  'LOST',
  'EXPIRED',
];

/** The five shown on the dashboard summary, in pipeline order. */
export const SUMMARY_STATUSES: ProposalStatus[] = [
  'DRAFT',
  'READY',
  'FINAL',
  'SUBMITTED',
  'WON',
];

export interface User {
  userId: string;
  email: string;
  displayName: string;
  jobTitle: string;
  role: Role;
  status: 'ACTIVE' | 'DISABLED';
  lastLoginAt: string | null;
}

export interface TemplateSummary {
  templateId: string;
  name: string;
  description: string;
  /** The approved "Is This the Right Fit?" bullets, shown as card guidance. */
  bestFor: string[];
  order: number;
  version: string;
  status: string;
}

export type ProposalMode = 'QUICK' | 'CUSTOM';

export interface ClientContext {
  clientSituation?: string;
  businessChallenge?: string;
  clientObjective?: string;
  desiredOutcome?: string;
  additionalContext?: string;
}

export interface ProposalConfiguration {
  clientCompanyName?: string;
  proposalDate?: string;
  validityDays?: number;
  validityDate?: string;
  accountOwnerName?: string;
  accountOwnerTitle?: string;
  accountOwnerEmail?: string;
  accountOwnerPhone?: string;
  opportunityContext?: string;
  clientContext?: ClientContext;
  paymentTermId?: string;
  paymentTerms?: string;
  currency?: 'PHP' | 'USD';
  scopeItemIds?: string[];
  approachPhaseIds?: string[];
  deliverableIds?: string[];
  commercialItemIds?: string[];
  durations?: Record<string, number | string>;
  amounts?: Record<string, number>;
  discounts?: Record<string, number>;
  totalAmount?: number;
  engagement?: Record<string, EngagementValue>;
  scope?: ConfigurableItem[];
  deliverables?: ConfigurableItem[];
  approachPhases?: ConfigurableItem[];
  commercialItems?: ConfigurableItem[];
  rightFit?: ConfigurableItem[];
  timelinePhases?: ConfigurableItem[];
  customScopeGroupTitle?: string;
  executiveSummary?: string;
  customAssumptions?: CustomAssumption[];
  showYearOneContractValue?: boolean;
  mode?: ProposalMode;
}

// --------------------------------------------------- family rules & items

export type FieldType = 'SINGLE_SELECT' | 'MULTI_SELECT' | 'INTEGER' | 'TEXT' | 'BOOLEAN';

export interface FieldOption {
  value: string;
  label: string;
  description?: string;
  mapsToScopeItem?: string;
}

export interface EngagementField {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  helpText?: string;
  options?: FieldOption[];
  allowOther?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
}

export interface EngagementSection {
  id: string;
  title: string;
  description: string;
  fields: EngagementField[];
}

export type CommercialModel = 'SINGLE_TOTAL' | 'ONE_TIME_AND_RECURRING';

export interface FamilyRules {
  familyId: string;
  sections: EngagementSection[];
  commercialModel: CommercialModel;
  commercialKinds?: Record<string, 'ONE_TIME' | 'RECURRING'>;
  recurringSuffix?: string;
  offersContractValue?: boolean;
  contractValueNote?: string;
  tierDrivesCommercialItems?: {
    fieldKey: string;
    itemByValue: Record<string, string>;
  };
}

export type ItemState = 'STANDARD' | 'MODIFIED' | 'CUSTOM';

export interface ConfigurableItem {
  id: string;
  standardValue: string | null;
  value: string;
  state: ItemState;
  included: boolean;
  standardDetail?: string | null;
  detail?: string;
}

export interface CommercialLine {
  id: string;
  label: string;
  kind: 'ONE_TIME' | 'RECURRING';
  gross: number | null;
  net: number | null;
  discountPercent: number;
  amountText: string;
}

export interface CommercialSummary {
  model: CommercialModel;
  lines: CommercialLine[];
  oneTimeTotal: number;
  recurringTotal: number;
  recurringSuffix: string | null;
  total: number | null;
  totalText: string;
  yearOneContractValue: number | null;
  currency: 'PHP' | 'USD';
}

export type EngagementValue = string | string[] | number | boolean | null;

export interface CustomAssumption {
  id: string;
  text: string;
}

export type DeviationKind =
  | 'MODIFIED_STANDARD_SCOPE'
  | 'CUSTOM_SCOPE'
  | 'EXCLUDED_STANDARD_SCOPE'
  | 'MODIFIED_DELIVERABLE'
  | 'CUSTOM_DELIVERABLE'
  | 'EXCLUDED_DELIVERABLE'
  | 'MODIFIED_RIGHT_FIT'
  | 'EXCLUDED_RIGHT_FIT'
  | 'MODIFIED_TIMELINE_ACTIVITY'
  | 'MODIFIED_EXECUTIVE_SUMMARY'
  | 'CUSTOM_ASSUMPTION'
  | 'CUSTOM_COMMERCIAL_ITEM'
  | 'MODIFIED_COMMERCIAL_ITEM'
  | 'CUSTOM_PAYMENT_TERMS';

export interface Deviation {
  kind: DeviationKind;
  label: string;
  count: number;
  step: 'client' | 'engagement' | 'scope' | 'timeline' | 'commercials' | 'review';
  examples: string[];
}

export interface LockedSection {
  id: string;
  title: string;
  reason: string;
  manifestKey: string;
}

export interface ReviewReport {
  deviations: { isBaseline: boolean; total: number; deviations: Deviation[] };
  lockedSections: LockedSection[];
  defaultExecutiveSummary: string;
}

export type CheckId =
  | 'CLIENT'
  | 'DATES'
  | 'ACCOUNT_OWNER'
  | 'ENGAGEMENT'
  | 'TIMELINE'
  | 'COMMERCIALS'
  | 'PAYMENT_TERMS'
  | 'PLACEHOLDERS';

export interface QualityFinding {
  check: CheckId;
  severity: 'BLOCKER' | 'WARNING';
  message: string;
  step: 'client' | 'engagement' | 'timeline' | 'commercials' | 'review';
  fields?: string[];
}

export interface QualityReport {
  status: 'READY_TO_GENERATE' | 'NEEDS_ATTENTION';
  checks: Array<{ check: CheckId; label: string; passed: boolean }>;
  findings: QualityFinding[];
  blockers: number;
  warnings: number;
  proposalNumber?: string;
}

// -------------------------------------------------------------- finalization

export type ReadinessArea =
  | 'CLIENT'
  | 'DATES'
  | 'ACCOUNT_OWNER'
  | 'ENGAGEMENT'
  | 'TIMELINE'
  | 'COMMERCIALS'
  | 'PAYMENT_TERMS'
  | 'TEMPLATE_VALIDITY'
  | 'PLACEHOLDERS';

export interface ReadinessItem {
  area: ReadinessArea;
  label: string;
  complete: boolean;
  attention: string | null;
  blocking: boolean;
  step: string;
  detail: string | null;
}

export interface ReadinessReport {
  /** 0–100. A usability indicator, never the gate. */
  percent: number;
  completed: ReadinessItem[];
  needsAttention: ReadinessItem[];
  blockers: ReadinessItem[];
  canFinalize: boolean;
}

export interface LeakageFinding {
  id: string;
  term: string;
  section: string;
  field: string;
  excerpt: string;
  step: string;
  acknowledged: boolean;
}

export interface LeakageReport {
  findings: LeakageFinding[];
  outstanding: LeakageFinding[];
  hasOutstanding: boolean;
  watchedTerms: string[];
  sourceProposalId: string | null;
  originalClientName: string | null;
}

export interface VersionValidationStatus {
  status: 'PASSED' | 'FAILED';
  criticalUnresolved: number;
  expectedSignature: number;
  criticalTokens: string[];
  readinessPercent: number;
  checkedAt: string;
}

export interface FinalizationReport {
  canFinalize: boolean;
  reasons: string[];
  readiness: ReadinessReport;
  leakage: LeakageReport;
  latestVersionValidation: VersionValidationStatus | null;
  proposalNumber: string;
  status: ProposalStatus;
}

export interface ProposalVersionSummary {
  version: number;
  createdAt: string;
  createdByUserId: string;
  templateVersion: string;
  documentKey: string | null;
  documentBytes: number | null;
  /** Absent on versions generated before placeholder validation existed. */
  validationStatus?: VersionValidationStatus;
}

/** Which parts of a source proposal a duplicate carries over. */
export interface DuplicateOptions {
  clientCompanyName?: string;
  retainScopeSelections?: boolean;
  retainDurations?: boolean;
  retainPricing?: boolean;
  retainCustomContent?: boolean;
  retainExecutiveSummary?: boolean;
}

export interface Proposal {
  proposalId: string;
  proposalNumber: string;
  templateId: string;
  templateName: string;
  templateVersion: string;
  status: ProposalStatus;
  clientCompanyName: string;
  accountOwnerUserId: string;
  accountOwnerName: string;
  accountOwnerTitle: string;
  investmentTotal: number | null;
  currency: 'PHP' | 'USD';
  configuration: ProposalConfiguration;
  revision: number;
  latestDocumentVersion: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Which statuses each status may move to. Mirrors the server's rule. */
export const STATUS_TRANSITIONS: Record<ProposalStatus, ProposalStatus[]> = {
  DRAFT: ['READY', 'EXPIRED'],
  READY: ['DRAFT', 'FINAL', 'EXPIRED'],
  FINAL: ['READY', 'SUBMITTED', 'EXPIRED'],
  SUBMITTED: ['WON', 'LOST', 'EXPIRED'],
  WON: [],
  LOST: ['SUBMITTED'],
  EXPIRED: ['DRAFT'],
};

export interface ProposalSummary {
  proposalId: string;
  proposalNumber: string;
  clientCompanyName: string;
  templateId: string;
  templateName: string;
  accountOwnerUserId: string;
  accountOwnerName: string;
  status: ProposalStatus;
  investmentTotal: number | null;
  currency: 'PHP' | 'USD';
  latestDocumentVersion: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentTerm {
  paymentTermId: string;
  /** What Sales sees in the dropdown. */
  displayName: string;
  /** Rendered verbatim into the approved payment-terms sentence. */
  templateText: string;
  sortOrder: number;
  isDefault: boolean;
  /** When true the configurator asks for free text instead of `templateText`. */
  allowsCustomText: boolean;
  status: 'ACTIVE' | 'RETIRED';
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------- ai

export type AIFunctionId =
  | 'generateClientExecutiveSummary'
  | 'improveExecutiveSummary'
  | 'rewriteScopeItem'
  | 'generateBusinessOutcomes'
  | 'improveClientContext'
  | 'proofreadProposal'
  | 'shortenSection'
  | 'makeMoreExecutive'
  | 'makeMoreTechnical'
  | 'makeMoreConsultative';

export interface AIFunctionInfo {
  id: AIFunctionId;
  label: string;
  section: string;
  description: string;
  /** Exactly what this function sends to the provider. Shown to the user. */
  sends: string[];
  requiresCurrentText: boolean;
}

export interface AIStatus {
  /** False whenever AI_PROVIDER=none, or a provider is missing credentials. */
  enabled: boolean;
  provider: string;
  model: string;
  functions: AIFunctionInfo[];
}

export interface AISuggestion {
  suggestionId: string;
  functionId: AIFunctionId;
  section: string;
  text: string;
  provider: string;
  model: string;
  createdAt: string;
  contextSent: string[];
  /** The model flagged a gap rather than filling it. */
  needsReview: boolean;
}

export interface GuardrailFinding {
  kind: string;
  match: string;
  explanation: string;
}

// ------------------------------------------------------------ administration

export type ApprovedContentCategory =
  | 'COMPANY_PROFILE'
  | 'WHY_LOTUS_TECH'
  | 'COMPANY_METRICS'
  | 'STANDARD_ASSUMPTIONS'
  | 'TERMS'
  | 'NEXT_STEPS'
  | 'CONTACT_INFORMATION'
  | 'OTHER';

/** Human labels for the categories, so the UI never shows a SCREAMING_KEY. */
export const CONTENT_CATEGORY_LABELS: Record<ApprovedContentCategory, string> = {
  COMPANY_PROFILE: 'Company profile',
  WHY_LOTUS_TECH: 'Why Lotus Tech',
  COMPANY_METRICS: 'Company metrics',
  STANDARD_ASSUMPTIONS: 'Standard assumptions',
  TERMS: 'Terms',
  NEXT_STEPS: 'Next steps',
  CONTACT_INFORMATION: 'Contact information',
  OTHER: 'Other approved content',
};

export interface ApprovedContent {
  contentId: string;
  templateId: string | null;
  title: string;
  category: ApprovedContentCategory;
  content: string;
  version: number;
  status: 'ACTIVE' | 'RETIRED';
  /** Quantitative corporate claims. Administrator-only, everywhere. */
  controlled: boolean;
  createdAt: string;
  createdByUserId: string | null;
  updatedAt: string;
  updatedByUserId: string | null;
}

export interface ApprovedContentVersion {
  contentId: string;
  version: number;
  title: string;
  category: ApprovedContentCategory;
  content: string;
  status: 'ACTIVE' | 'RETIRED';
  createdAt: string;
  createdByUserId: string | null;
  note: string;
}

export interface AdminTemplateVersion {
  version: string;
  status: 'ACTIVE' | 'SUPERSEDED';
  processedKey: string;
  sourceSha256: string;
  createdAt: string;
  isCurrent: boolean;
}

export interface AdminTemplate {
  templateId: string;
  name: string;
  description: string;
  order: number;
  status: 'ACTIVE' | 'DRAFT' | 'RETIRED';
  currentVersion: string;
  sourceSha256: string;
  sourceFile: string;
  processedFile: string | null;
  createdAt: string;
  updatedAt: string;
  versions: AdminTemplateVersion[];
}

export interface ConfigurationSetting {
  key: string;
  label: string;
  description: string;
  group: 'PROPOSAL' | 'COMPANY' | 'TAX' | 'SYSTEM';
  control: 'NUMBER' | 'TEXT' | 'SELECT' | 'MULTI_SELECT' | 'BOOLEAN';
  options?: Array<{ value: string; label: string }>;
  advanced: boolean;
  value: unknown;
  isDefault: boolean;
  updatedAt: string | null;
  updatedByUserId: string | null;
}

export interface StandardContent {
  executiveSummary?: string;
  promiseLine?: string;
  rightFitIntro?: string;
  rightFit?: string[];
  differentiators?: Array<{ title: string; meaning: string }>;
  metricsLine?: string;
  assumptions?: string[];
  nextSteps?: string[];
  nextStepsClosing?: string;
  contactLine?: string;
  [key: string]: unknown;
}

export interface TemplateManifest {
  id: string;
  name: string;
  description: string;
  scopeIntro: string;
  scopeGroups: Array<{
    id: string;
    title: string;
    items: Array<{ id: string; text: string; includedByDefault: boolean }>;
  }>;
  approachPhases: Array<{ id: string; number: string; name: string; description: string }>;
  deliverables: Array<{
    id: string;
    name: string;
    description: string;
    includedByDefault: boolean;
  }>;
  timelinePhases: Array<{
    id: string;
    phase: string;
    activities: string;
    durationKind: 'DURATION' | 'ONGOING' | 'CADENCE';
  }>;
  commercialItems: Array<{
    id: string;
    label: string;
    optional: boolean;
    includedByDefault: boolean;
  }>;
  commercialTotalLabel: string;
  standardContent: StandardContent;
}

// --------------------------------------------------------------- endpoints

export const endpoints = {
  session: () => api.get<{ user: User; csrfToken: string }>('/api/auth/session'),
  login: (email: string, password: string) =>
    api.post<{ user: User; csrfToken: string }>('/api/auth/login', { email, password }),
  logout: () => api.post<{ ok: true }>('/api/auth/logout'),

  templates: () => api.get<{ templates: TemplateSummary[] }>('/api/templates'),
  templateManifest: (templateId: string) =>
    api.get<{
      templateId: string;
      version: string;
      manifest: TemplateManifest;
      rules: FamilyRules;
    }>(`/api/templates/${encodeURIComponent(templateId)}`),
  paymentTerms: () => api.get<{ paymentTerms: PaymentTerm[] }>('/api/payment-terms'),

  proposalSummary: (scope: 'mine' | 'all' = 'mine') =>
    api.get<{ counts: Record<ProposalStatus, number> }>(
      `/api/proposals/summary?scope=${scope}`,
    ),
  proposals: (query: { status?: ProposalStatus; scope?: 'mine' | 'all'; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.scope) params.set('scope', query.scope);
    if (query.limit) params.set('limit', String(query.limit));
    const search = params.toString();
    return api.get<{ items: ProposalSummary[]; cursor: string | null }>(
      `/api/proposals${search ? `?${search}` : ''}`,
    );
  },
  proposal: (proposalId: string) =>
    api.get<{ proposal: Proposal; allowedTransitions: ProposalStatus[] }>(
      `/api/proposals/${encodeURIComponent(proposalId)}`,
    ),
  createProposal: (templateId: string, clientCompanyName?: string, mode?: ProposalMode) =>
    api.post<{ proposal: Proposal }>('/api/proposals', {
      templateId,
      ...(clientCompanyName ? { clientCompanyName } : {}),
      ...(mode ? { mode } : {}),
    }),
  saveProposal: (
    proposalId: string,
    configuration: ProposalConfiguration,
    revision: number,
    signal?: AbortSignal,
  ) =>
    api.patch<{ proposal: Proposal; savedAt: string }>(
      `/api/proposals/${encodeURIComponent(proposalId)}`,
      { configuration, revision },
      { signal },
    ),
  duplicateProposal: (proposalId: string, options: DuplicateOptions = {}) =>
    api.post<{ proposal: Proposal }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/duplicate`,
      options,
    ),
  changeStatus: (proposalId: string, status: ProposalStatus) =>
    api.post<{ proposal: Proposal; allowedTransitions: ProposalStatus[] }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/status`,
      { status },
    ),
  review: (proposalId: string) =>
    api.get<ReviewReport>(`/api/proposals/${encodeURIComponent(proposalId)}/review`),
  commercials: (proposalId: string) =>
    api.get<CommercialSummary>(
      `/api/proposals/${encodeURIComponent(proposalId)}/commercials`,
    ),
  quality: (proposalId: string) =>
    api.get<QualityReport>(`/api/proposals/${encodeURIComponent(proposalId)}/quality`),
  readiness: (proposalId: string) =>
    api.get<FinalizationReport>(`/api/proposals/${encodeURIComponent(proposalId)}/readiness`),
  leakage: (proposalId: string) =>
    api.get<LeakageReport>(`/api/proposals/${encodeURIComponent(proposalId)}/leakage`),
  acknowledgeLeakage: (proposalId: string, findingIds: string[]) =>
    api.post<{ proposal: Proposal; leakage: LeakageReport }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/leakage/acknowledge`,
      { findingIds },
    ),
  versions: (proposalId: string) =>
    api.get<{ versions: ProposalVersionSummary[] }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/versions`,
    ),
  deleteProposal: (proposalId: string) =>
    api.del<{ ok: true }>(`/api/proposals/${encodeURIComponent(proposalId)}`),
  generateDocument: (proposalId: string) =>
    api.post<{
      version: number;
      documentBytes: number;
      scan: {
        ok: boolean;
        summary: {
          /** Occurrences, not findings: one token appearing twice counts twice. */
          totalCritical: number;
          totalExpectedSignature: number;
          criticalTokens: string[];
        };
      };
    }>(`/api/proposals/${encodeURIComponent(proposalId)}/document`),
  downloadUrl: (proposalId: string) =>
    api.get<{ url: string; fileName: string; version: number; expiresInSeconds: number }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/document`,
    ),

  aiStatus: () => api.get<AIStatus>('/api/ai/status'),
  aiSuggest: (proposalId: string, functionId: AIFunctionId, currentText?: string) =>
    api.post<{ suggestion: AISuggestion }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/ai/suggest`,
      { functionId, ...(currentText === undefined ? {} : { currentText }) },
    ),
  /** Records what the user did with a suggestion. Applying itself is a normal save. */
  aiOutcome: (
    proposalId: string,
    functionId: AIFunctionId,
    applied: boolean,
    suggestion?: AISuggestion,
  ) =>
    api.post<{ recorded: true }>(
      `/api/proposals/${encodeURIComponent(proposalId)}/ai/outcome`,
      {
        functionId,
        applied,
        ...(suggestion
          ? { provider: suggestion.provider, model: suggestion.model }
          : {}),
      },
    ),

  users: () => api.get<{ users: User[] }>('/api/admin/users'),
  createUser: (input: {
    email: string;
    displayName: string;
    jobTitle: string;
    role: Role;
    password: string;
  }) => api.post<{ user: User }>('/api/admin/users', input),
  updateUser: (
    userId: string,
    patch: {
      displayName?: string;
      jobTitle?: string;
      role?: Role;
      status?: 'ACTIVE' | 'DISABLED';
    },
  ) => api.patch<{ user: User }>(`/api/admin/users/${encodeURIComponent(userId)}`, patch),

  adminTemplates: () =>
    api.get<{ templates: AdminTemplate[] }>('/api/admin/templates'),
  setTemplateStatus: (templateId: string, status: 'ACTIVE' | 'RETIRED') =>
    api.post<{ template: AdminTemplate }>(
      `/api/admin/templates/${encodeURIComponent(templateId)}/status`,
      { status },
    ),
  setTemplateDefaultVersion: (templateId: string, version: string) =>
    api.post<{ template: AdminTemplate }>(
      `/api/admin/templates/${encodeURIComponent(templateId)}/default-version`,
      { version },
    ),

  approvedContent: () =>
    api.get<{
      content: ApprovedContent[];
      categories: ApprovedContentCategory[];
      controlledCategories: ApprovedContentCategory[];
    }>('/api/admin/content'),
  approvedContentVersions: (contentId: string) =>
    api.get<{ versions: ApprovedContentVersion[] }>(
      `/api/admin/content/${encodeURIComponent(contentId)}/versions`,
    ),
  createApprovedContent: (input: {
    title: string;
    category: ApprovedContentCategory;
    content: string;
    templateId?: string | null;
    note?: string;
  }) => api.post<{ content: ApprovedContent }>('/api/admin/content', input),
  updateApprovedContent: (
    contentId: string,
    patch: {
      title?: string;
      category?: ApprovedContentCategory;
      content?: string;
      templateId?: string | null;
      note?: string;
    },
  ) =>
    api.patch<{ content: ApprovedContent }>(
      `/api/admin/content/${encodeURIComponent(contentId)}`,
      patch,
    ),
  setApprovedContentStatus: (contentId: string, status: 'ACTIVE' | 'RETIRED') =>
    api.post<{ content: ApprovedContent }>(
      `/api/admin/content/${encodeURIComponent(contentId)}/status`,
      { status },
    ),

  createPaymentTerm: (input: {
    displayName: string;
    templateText: string;
    sortOrder?: number;
    isDefault?: boolean;
    allowsCustomText?: boolean;
  }) => api.post<{ paymentTerm: PaymentTerm }>('/api/admin/payment-terms', input),
  updatePaymentTerm: (
    paymentTermId: string,
    patch: {
      displayName?: string;
      templateText?: string;
      sortOrder?: number;
      isDefault?: boolean;
      status?: 'ACTIVE' | 'RETIRED';
    },
  ) =>
    api.patch<{ paymentTerm: PaymentTerm }>(
      `/api/admin/payment-terms/${encodeURIComponent(paymentTermId)}`,
      patch,
    ),
  deletePaymentTerm: (paymentTermId: string) =>
    api.del<{ ok: true }>(`/api/admin/payment-terms/${encodeURIComponent(paymentTermId)}`),

  updateConfiguration: (changes: Record<string, unknown>) =>
    api.patch<{ configuration: ConfigurationSetting[] }>('/api/admin/configuration', {
      changes,
    }),
  auditEvents: (limit = 50) =>
    api.get<{ events: Array<Record<string, unknown>> }>(`/api/admin/audit?limit=${limit}`),
  adminConfiguration: () =>
    api.get<{
      configuration: ConfigurationSetting[];
      paymentTerms: PaymentTerm[];
    }>('/api/admin/configuration'),
};
